import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChatJessMessage, FileAttachment } from "./types";
import styles from "./styles.module.scss";
import { mockChatJessMessages } from "../../../mocks/chatJess/mockChatJessMessages";
import { useChatJessWebSocket } from "../../../hooks/ChatJess/useChatJessWebSocket";
import jessImage from "../../../assets/images/jess.png";
import jessProfileIcon from "../../../assets/navbar/iconperfiljesss.svg";
import jessProfileIconOff from "../../../assets/navbar/iconperfiljesssOff.svg";
import jessNameLogo from "../../../assets/navbar/nomejesss.svg";
import jessDesktopAppIcon from "../../../assets/logos/Icone Jessswhite.svg";
import { logger } from "../../../utils/logger";
import { formatFileSize } from "../../../utils";
import Picker, { EmojiClickData, Theme } from "emoji-picker-react";
import DetailModal from "./components/DetailModal";
import { ResponsiveNavbar } from "../../../components/ResponsiveNavbar/ResponsiveNavbar";
import { useProfile } from "../../../hooks/UserHooks";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import AudioRecorder from "./components/AudioRecorder";
import ChatJessMessages from "./ChatJessMessages";
import {
  fetchAgentConnectionStatus,
  AgentConnectionStatus,
} from "../../../api/agent";
import {
  uploadFile,
  downloadFile,
  isAgentProxyUrl,
} from "../../../services/process/api";
import { getFileIcon, getFileIconColor } from "../../../utils/fileIcons";
import Joyride from "react-joyride";
import { useInteractiveTour } from "../../../hooks/useInteractiveTour";
import { useReward } from "react-rewards";
import JessModal from "../../../components/JessModal";
import TourAnimatedCursor from "../../../components/TourAnimatedCursor";
import FeedbackPanel from "./components/FeedbackPanel";
import { KpiPanel, KpiDashboardData } from "./components/KpiPanel";
import { resolveI18nLabel } from "./components/KpiPanel/types";
import { detectLanguageByTimezone } from "../../../utils/languageDetection";
import { motion, AnimatePresence } from "framer-motion";

const ChatJessPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Hooks de responsividade
  const breakpointInfo = useBreakpoint();

  // Verificar se o mock está ativo - considerar localStorage também
  const useMock =
    process.env.REACT_APP_CHATJESS_USE_MOCK === "true" ||
    localStorage.getItem("CHATJESS_MOCK_MODE") === "true";

  // Refs - Defined early to be used in callbacks
  const mainContentRef = useRef<HTMLDivElement>(null);
  const chatHistoryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chatHistoryRetryIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const scrollToBottom = useCallback((smooth = false) => {
    requestAnimationFrame(() => {
      if (mainContentRef.current) {
        const element = mainContentRef.current;
        element.scrollTo({
          top: element.scrollHeight,
          behavior: smooth ? "smooth" : "instant",
        });
      }
    });
  }, []);

  // Local state for loading and timeout
  const [isLoadingChatHistory, setIsLoadingChatHistory] = useState(false);
  const [chatHistoryTimeout, setChatHistoryTimeout] = useState(false);
  const [chatHistoryRetryCount, setChatHistoryRetryCount] = useState(0);

  // Estados do WebSocket usando o hook customizado
  const {
    messages: wsMessages,
    isTyping: wsIsTyping,
    connection,
    sendMessage: wsSendMessage,
    sendFeedbackResponse,
    sendChatConnected,
    reconnect,
    isConnected,
    isWaitingForResponse,
  } = useChatJessWebSocket({
    initialMessages: useMock ? mockChatJessMessages : [], // Carregar mensagens iniciais apenas no mock
    onNewMessage: () => {
      scrollToBottom();
    },
    onFeedbackRequest: () => {},
    onError: (error) => {
      logger.error("Erro no WebSocket do ChatJess:", error);
    },
    onChatHistoryReceived: () => {
      setIsLoadingChatHistory(false);
      setChatHistoryTimeout(false);
      setChatHistoryRetryCount(0);

      // Clear all timers
      if (chatHistoryTimeoutRef.current) {
        clearTimeout(chatHistoryTimeoutRef.current);
        chatHistoryTimeoutRef.current = null;
      }
      if (chatHistoryRetryIntervalRef.current) {
        clearInterval(chatHistoryRetryIntervalRef.current);
        chatHistoryRetryIntervalRef.current = null;
      }

      // Scroll to bottom after chat history is loaded with multiple attempts for mobile ~temporario~
      setTimeout(() => {
        scrollToBottom();
        // Second attempt for mobile rendering delay ~temporario~
        setTimeout(() => scrollToBottom(), 100);
      }, 200);
    },
    onKpiDashboardUpdate: (payload) => {
      setKpiWsData(payload);
    },
  });

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [wsMessages, scrollToBottom]);

  // Scroll to bottom on mount if there are messages (for chat history)
  // Multiple attempts ensure mobile rendering is complete ~temporario~
  useEffect(() => {
    if (wsMessages.length > 0 && mainContentRef.current) {
      // Use timeout to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        scrollToBottom();
        // Additional scroll attempts for mobile ~temporario~
        setTimeout(() => scrollToBottom(), 150);
        setTimeout(() => scrollToBottom(), 300);
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Estados locais da UI
  const [kpiPanelOpen, setKpiPanelOpen] = useState(false);
  const [kpiWsData, setKpiWsData] = useState<KpiDashboardData | null>(null);
  const kpiLang = useMemo(() => detectLanguageByTimezone(), []);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState<ChatJessMessage | null>(null);
  const [agentConnectionStatus, setAgentConnectionStatus] =
    useState<AgentConnectionStatus | null>(null);
  const [isLoadingAgentStatus, setIsLoadingAgentStatus] = useState(true);
  const [showOfflineNotification, setShowOfflineNotification] = useState(false);
  const [hasUserSentMessage, setHasUserSentMessage] = useState(false);
  const [hasClosedOfflineIndicator, setHasClosedOfflineIndicator] =
    useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved === "dark" ? true : false;
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebar");
    // Usar nosso hook personalizado - mobile/tablet começa recolhido
    if (breakpointInfo.isMobile || breakpointInfo.isTablet) {
      return saved !== "expanded"; // Começa recolhido no mobile/tablet se não houver estado salvo ou se estiver salvo como collapsed
    }
    return saved === "collapsed"; // Comportamento padrão para desktop/widescreen
  });
  const [currentMessage, setCurrentMessage] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [suggestionText, setSuggestionText] = useState("");

  const handleSuggestionTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setSuggestionText(e.target.value);
  };

  const [debugNotification, setDebugNotification] = useState<{
    show: boolean;
    message: string;
    type: "success" | "info" | "warning" | "error";
  }>({ show: false, message: "", type: "info" });

  // Estados para emoji picker
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);

  // Estados para modal de anexos
  const [showAttachmentModal, setShowAttachmentModal] =
    useState<boolean>(false);

  // Estados para gerenciar anexos
  const [pendingAttachments, setPendingAttachments] = useState<
    FileAttachment[]
  >([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // Dismiss drag overlay on Escape (when user gives up dropping files)
  useEffect(() => {
    if (!isDragOver) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsDragOver(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isDragOver]);

  // Estados para o painel "Passo a Passo" na welcome animation
  const [stepByStepPanelOpen, setStepByStepPanelOpen] = useState(false);
  const [activeStepByStepTab, setActiveStepByStepTab] = useState("report");

  // Estado para notificação do tour
  const [showTourNotification, setShowTourNotification] = useState(false);

  // Aplicar classe para afastar componentes quando painel estiver aberto
  const pageWrapperClass = `${styles.pageWrapper} ${!isDarkTheme ? `${styles.lightTheme} lightTheme` : ""} ${feedbackOpen || stepByStepPanelOpen ? styles.feedbackPanelOpen : ""} ${kpiPanelOpen ? styles.kpiPanelOpen : ""}`;
  const [imageContextModal, setImageContextModal] = useState<{
    show: boolean;
    attachmentId: string;
    position: { x: number; y: number };
  }>({
    show: false,
    attachmentId: "",
    position: { x: 0, y: 0 },
  });

  // Estados das abas do painel de feedback
  const [activeTab, setActiveTab] = useState<"report" | "attachments">(
    "report",
  );

  // Estados para modais de detalhes
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailModalTitle, setDetailModalTitle] = useState("");
  const [detailModalContent, setDetailModalContent] = useState("");

  const [displayMessages, setDisplayMessages] = useState<ChatJessMessage[]>([]);

  // Hook para obter informações do usuário logado (DEVE VIR ANTES do useInteractiveTour)
  const { userProfile } = useProfile();

  // Hook do tour interativo (APÓS profile estar disponível)
  const {
    isRunning: isTourRunning,
    currentStep,
    steps: tourSteps,
    isWaitingForInteraction,
    totalSteps,
    stopTour,
    nextStep: tourNextStep,
    previousStep: tourPreviousStep,
    skipTour,
    canGoNext,
    canGoBack,
    getCurrentTarget,
    handleJoyrideCallback,
    hasUserInteractedWithTour,
  } = useInteractiveTour({
    userId: userProfile?.userName || "anonymous",
    onTourComplete: () => {
      // IMEDIATAMENTE salvar no localStorage
      localStorage.setItem("hasSeenWelcomeAnimation", "true");

      // IMEDIATAMENTE esconder a tela usando função de estado para garantir atualização
      setShowWelcomeAnimation(() => {
        return false;
      });
      setShowConfettiIcon(false);
      setHasTriggeredConfetti(false);
      setWelcomeStep(0);
      setShowTyping1(false);
      setShowMessage1(false);
      setShowTyping2(false);
      setShowMessage2(false);
      setShowMessage3(false);

      // Verificação final mais rápida e robusta
      setTimeout(() => {
        setShowWelcomeAnimation(false);
      }, 100); // Reduzido para 100ms para ser mais responsivo
    },
    onTourSkip: () => {
      // Forçar esconder a tela imediatamente
      setShowWelcomeAnimation(false);
      setShowConfettiIcon(false);
      setHasTriggeredConfetti(false);
      setWelcomeStep(0);
      setShowTyping1(false);
      setShowMessage1(false);
      setShowTyping2(false);
      setShowMessage2(false);
      setShowMessage3(false);

      // Salvar no localStorage
      localStorage.setItem("hasSeenWelcomeAnimation", "true");
    },
    onStepComplete: () => {
      // NÃO esconder a tela de boas-vindas no primeiro step
      // A tela só deve ser escondida quando o tour for completado ou pulado
    },
    config: {
      autoStart: false, // Não iniciar automaticamente
      locale: "pt-BR",
    },
  });

  // Estado para controlar a animação de boas-vindas para novos usuários
  const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(() => {
    // Verificar no momento da inicialização se deve mostrar
    return false; // Sempre começar com false e deixar o useEffect controlar
  });
  const [showConfettiIcon, setShowConfettiIcon] = useState(false);
  const [hasTriggeredConfetti, setHasTriggeredConfetti] = useState(false);

  // Estados para controlar a sequência de mensagens da Jess
  const [welcomeStep, setWelcomeStep] = useState(0);
  const [showTyping1, setShowTyping1] = useState(false);
  const [showMessage1, setShowMessage1] = useState(false);
  const [showTyping2, setShowTyping2] = useState(false);
  const [showMessage2, setShowMessage2] = useState(false);
  const [showMessage3, setShowMessage3] = useState(false);

  // Hook do react-rewards para confetti
  const { reward: confettiReward } = useReward("confettiReward", "confetti", {
    elementCount: 50, // Quantidade de confetti
    spread: 90, // Espalhamento horizontal
    lifetime: 1200, // Tempo de vida muito maior para cair até embaixo
    startVelocity: 25, // Velocidade inicial reduzida para durar mais
    decay: 0.96, // Decay menor para cair mais devagar
    angle: 90, // Ângulo para baixo
    colors: [
      "#83de80",
      "#ff6b6b",
      "#4ecdc4",
      "#45b7d1",
      "#96ceb4",
      "#feca57",
      "#ff9ff3",
      "#54a0ff",
      "#ff7675",
      "#74b9ff",
    ],
    position: "fixed", // Posição fixa na tela
    zIndex: 9999, // Z-index alto para ficar sobre tudo
  });

  // Prevent body scroll when input is focused on mobile/tablet to keep navbar fixed
  useEffect(() => {
    if (!breakpointInfo.isMobile && !breakpointInfo.isTablet) {
      return; // Only apply on mobile/tablet
    }

    if (isInputFocused) {
      // Prevent body from scrolling when keyboard opens
      const scrollY = window.scrollY;
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalTop = document.body.style.top;
      const originalWidth = document.body.style.width;

      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";

      return () => {
        // Restore original styles when input loses focus
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = originalWidth;
        window.scrollTo(0, scrollY);
      };
    }
  }, [isInputFocused, breakpointInfo.isMobile, breakpointInfo.isTablet]);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatHistoryLoadedRef = useRef(false);
  const MESSAGE_INPUT_MAX_HEIGHT_PX = 200; // Matches .messageInput max-height in styles

  // Auto-resize textarea as content grows, up to MESSAGE_INPUT_MAX_HEIGHT_PX
  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    const capped = Math.min(textarea.scrollHeight, MESSAGE_INPUT_MAX_HEIGHT_PX);
    textarea.style.height = `${Math.max(capped, 24)}px`;
  }, [currentMessage]);

  const maxRetryAttempts = 5; // Maximum retry attempts
  const retryIntervalMs = 3000; // 3 seconds between retries
  const finalTimeoutMs = 10000; // 10 seconds total timeout

  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  // Tipos de arquivo suportados - based on allowed extensions
  const ALLOWED_FILE_TYPES = {
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".png": "image/png",
    ".gif": "image/gif",
    ".webp": "image/webp",
    ".pdf": "application/pdf",
    ".txt": "text/plain",
    ".csv": "text/csv",
    ".mpeg": "audio/mpeg",
    ".mp3": "audio/mp3",
    ".ogg": "audio/ogg",
    ".wav": "audio/wav",
    ".webm": "audio/webm",
    ".mp4": "audio/mp4",
  };

  // Função para verificar se é um novo usuário (SIMPLIFICADA - sem logs excessivos)
  const isNewUser = useCallback(() => {
    const hasSeenWelcome = localStorage.getItem("hasSeenWelcomeAnimation");
    const hasCompletedTour = hasUserInteractedWithTour();
    const isNew = !hasSeenWelcome && !hasCompletedTour;

    return isNew;
  }, [hasUserInteractedWithTour, userProfile?.userName, showWelcomeAnimation]);

  // Effect para verificar se deve mostrar a animação de boas-vindas
  useEffect(() => {
    const isUserNew = isNewUser();

    const shouldShowWelcome =
      isUserNew &&
      !isLoadingAgentStatus &&
      !isLoadingChatHistory &&
      displayMessages.length === 0 &&
      !hasTriggeredConfetti;

    if (shouldShowWelcome) {
      setShowWelcomeAnimation(true);
      setHasTriggeredConfetti(true); // Marcar que já foi executado

      // Mostrar o ícone de confetti após 1 segundo
      setTimeout(() => {
        setShowConfettiIcon(true);

        // Primeiro estouro de confetti após 500ms
        setTimeout(() => {
          confettiReward();
        }, 500);

        // Segundo estouro após 800ms
        setTimeout(() => {
          confettiReward();
        }, 800);

        // Terceiro estouro após 1200ms
        setTimeout(() => {
          confettiReward();
        }, 1200);

        // Iniciar sequência de mensagens simplificada - apenas digitação + mensagem
        setTimeout(() => {
          setWelcomeStep(1);

          // Mostrar balão de digitação imediatamente
          setShowTyping1(true);

          // Depois de 1.5 segundos, mostrar mensagem de boas-vindas
          setTimeout(() => {
            setShowTyping1(false);
            setShowMessage1(true);
          }, 1500);
        }, 800); // Iniciar rápido
      }, 1000);
    } else if (showWelcomeAnimation && !isUserNew) {
      // Se a tela estava sendo exibida mas agora o usuário não é mais novo, esconder
      setShowWelcomeAnimation(false);
      setShowConfettiIcon(false);
      setHasTriggeredConfetti(false);
      setWelcomeStep(0);
      setShowTyping1(false);
      setShowMessage1(false);
      setShowTyping2(false);
      setShowMessage2(false);
      setShowMessage3(false);
    }

    // Verificação adicional para casos onde a tela pode estar "presa"
    if (showWelcomeAnimation) {
      const hasSeenWelcome = localStorage.getItem("hasSeenWelcomeAnimation");
      const hasCompletedTour = hasUserInteractedWithTour();

      if (hasSeenWelcome || hasCompletedTour) {
        setShowWelcomeAnimation(false);
        setShowConfettiIcon(false);
        setHasTriggeredConfetti(false);
        setWelcomeStep(0);
        setShowTyping1(false);
        setShowMessage1(false);
        setShowTyping2(false);
        setShowMessage2(false);
        setShowMessage3(false);
      }
    }
  }, [
    isLoadingAgentStatus,
    isLoadingChatHistory,
    displayMessages.length,
    hasTriggeredConfetti,
    showWelcomeAnimation,
    // REMOVIDO isNewUser para evitar loop infinito
  ]);

  // Effect para verificação inicial (só executa uma vez)
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem("hasSeenWelcomeAnimation");
    const hasCompletedTour = hasUserInteractedWithTour();
    const isUserNewNow = !hasSeenWelcome && !hasCompletedTour;

    // Se o usuário não é mais novo mas a tela está sendo exibida, forçar esconder
    if (!isUserNewNow && showWelcomeAnimation) {
      setShowWelcomeAnimation(false);
      setShowConfettiIcon(false);
      setHasTriggeredConfetti(false);
      setWelcomeStep(0);
      setShowTyping1(false);
      setShowMessage1(false);
      setShowTyping2(false);
      setShowMessage2(false);
      setShowMessage3(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect específico para monitorar mudanças do tour (SIMPLIFICADO)
  useEffect(() => {
    // Só executa se a tela estiver sendo exibida
    if (!showWelcomeAnimation) return;

    const hasCompletedTour = hasUserInteractedWithTour();
    const hasSeenWelcome = localStorage.getItem("hasSeenWelcomeAnimation");

    if (hasCompletedTour || hasSeenWelcome) {
      setShowWelcomeAnimation(false);
      setShowConfettiIcon(false);
      setHasTriggeredConfetti(false);
      setWelcomeStep(0);
      setShowTyping1(false);
      setShowMessage1(false);
      setShowTyping2(false);
      setShowMessage2(false);
      setShowMessage3(false);
    }
  }, [showWelcomeAnimation]); // Só depende do showWelcomeAnimation

  // Effect de limpeza final - executa periodicamente para garantir consistência
  useEffect(() => {
    const interval = setInterval(() => {
      if (showWelcomeAnimation) {
        const hasSeenWelcome = localStorage.getItem("hasSeenWelcomeAnimation");
        const hasCompletedTour = hasUserInteractedWithTour();

        if (hasSeenWelcome || hasCompletedTour) {
          setShowWelcomeAnimation(false);
          setShowConfettiIcon(false);
          setHasTriggeredConfetti(false);
          setWelcomeStep(0);
          setShowTyping1(false);
          setShowMessage1(false);
          setShowTyping2(false);
          setShowMessage2(false);
          setShowMessage3(false);
        }
      }
    }, 2000); // Verifica a cada 2 segundos

    return () => clearInterval(interval);
  }, [showWelcomeAnimation, hasUserInteractedWithTour]);

  const isValidFileType = (file: File): boolean => {
    // First check by file extension
    const fileName = file.name.toLowerCase();
    const extension = fileName.substring(fileName.lastIndexOf("."));

    // Check if the extension is in our allowed list
    if (!Object.hasOwn(ALLOWED_FILE_TYPES, extension)) {
      return false;
    }

    // Then verify the mime type matches
    const expectedMimeType =
      ALLOWED_FILE_TYPES[extension as keyof typeof ALLOWED_FILE_TYPES];

    // For CSV files, browser might report different MIME types
    if (extension === ".csv") {
      return (
        file.type === "text/csv" ||
        file.type === "application/vnd.ms-excel" ||
        file.type === "text/plain" ||
        file.type === ""
      );
    }

    // For other files, check if the mime type matches or starts with the expected type
    return (
      file.type === expectedMimeType ||
      file.type.startsWith(expectedMimeType.split("/")[0] + "/")
    );
  };

  const getAllowedExtensions = (): string => {
    return Object.keys(ALLOWED_FILE_TYPES).join(", ");
  };

  // Function to compress image if too large
  const compressImage = (file: File, maxSizeMB: number = 5): Promise<File> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions to keep aspect ratio - increased limits for better quality
        let { width, height } = img;
        const maxWidth = 2560; // Increased from 1920 for better quality
        const maxHeight = 1440; // Increased from 1080 for better quality

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        // Use better rendering for higher quality
        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          // Use better interpolation for JPEG images
          ctx.globalCompositeOperation = "source-over";
          ctx.drawImage(img, 0, 0, width, height);
        }

        // Try different quality levels to get under size limit
        const tryCompress = (quality: number): void => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error(t("chatjess.errors.failedToCompressImage")));
                return;
              }

              const targetSize = maxSizeMB * 1024 * 1024;

              if (blob.size <= targetSize || quality <= 0.3) {
                // Changed minimum quality from 0.1 to 0.3
                // Create new file with compressed data
                const compressedFile = new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                // Try with lower quality - smaller steps for better quality control
                tryCompress(quality - 0.05); // Changed from 0.1 to 0.05 for finer control
              }
            },
            file.type,
            quality,
          );
        };

        tryCompress(0.92); // Start with 92% quality instead of 80%
      };

      img.onerror = () =>
        reject(new Error(t("chatjess.errors.failedToLoadImage")));
      img.src = URL.createObjectURL(file);
    });
  };

  const generateThumbnail = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith("image/")) {
        resolve("");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Increased thumbnail size for better quality
          const maxWidth = 300; // Further increased for better quality
          const maxHeight = 300; // Further increased for better quality
          let { width, height } = img;

          // Calculate aspect ratio preserving dimensions
          const aspectRatio = width / height;

          if (width > height) {
            if (width > maxWidth) {
              width = maxWidth;
              height = width / aspectRatio;
            }
          } else {
            if (height > maxHeight) {
              height = maxHeight;
              width = height * aspectRatio;
            }
          }

          // Ensure dimensions are integers
          width = Math.round(width);
          height = Math.round(height);

          canvas.width = width;
          canvas.height = height;

          // Use better rendering for thumbnail
          if (ctx) {
            // Clear canvas with transparent background
            ctx.clearRect(0, 0, width, height);

            // Set high quality rendering
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";

            // Use better interpolation for JPEG thumbnails
            ctx.globalCompositeOperation = "source-over";

            // Draw image with high quality
            ctx.drawImage(img, 0, 0, width, height);
          }

          // Generate high quality JPEG thumbnail
          resolve(canvas.toDataURL("image/jpeg", 0.9)); // Increased quality to 0.9
        };
        img.onerror = () => reject(new Error("Erro ao carregar imagem"));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error("Erro ao ler arquivo"));
      reader.readAsDataURL(file);
    });
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const processFile = async (
    file: File,
    fileCategory: "document" | "image" | "audio",
  ): Promise<FileAttachment> => {
    if (!isValidFileType(file)) {
      const fileName = file.name.toLowerCase();
      const extension = fileName.substring(fileName.lastIndexOf("."));

      throw new Error(
        t("chatjess.attachments.unsupportedFileType", {
          extension: extension || t("chatjess.attachments.unknownExtension"),
          allowedTypes: getAllowedExtensions(),
        }),
      );
    }

    let processedFile = file;

    if (fileCategory === "image" && file.size > 8 * 1024 * 1024) {
      try {
        processedFile = await compressImage(file, 8);
        showDebugNotification(
          `📦 Imagem comprimida: ${formatFileSize(file.size)} → ${formatFileSize(processedFile.size)}`,
          "info",
        );
      } catch (error) {
        logger.warn("Failed to compress image, using original:", error);
        processedFile = file;
      }
    }

    if (processedFile.size > MAX_FILE_SIZE) {
      throw new Error(
        `Arquivo muito grande. Máximo permitido: ${formatFileSize(MAX_FILE_SIZE)}`,
      );
    }

    let thumbnail = "";
    try {
      if (fileCategory === "image") {
        thumbnail = await generateThumbnail(processedFile);
      }
    } catch (error) {
      logger.debug("Failed to generate thumbnail:", error);
    }

    const attachment: FileAttachment = {
      id: Date.now().toString() + Math.random().toString(36).substring(2, 11),
      name: processedFile.name,
      size: processedFile.size,
      type: processedFile.type,
      thumbnail,
      isUploading: true,
      uploadProgress: 0,
    };

    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 2000, 3000];
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const uploadResult = await uploadFile(processedFile);
        attachment.url = uploadResult.url;
        attachment.isUploading = false;
        attachment.uploadProgress = 100;

        if (fileCategory === "image" && attachment.url) {
          const img = new Image();
          img.onload = () => {};
          img.onerror = () => {
            logger.warn("Image URL validation failed:", attachment.url);
          };
          img.src = attachment.url;
        }

        return attachment;
      } catch (error) {
        lastError =
          error instanceof Error
            ? error
            : new Error(t("chatjess.errors.unknownError"));
        logger.warn(`Upload attempt ${attempt + 1} failed:`, lastError.message);

        if (attempt < MAX_RETRIES - 1) {
          await new Promise((resolve) =>
            setTimeout(resolve, RETRY_DELAYS[attempt]),
          );
          showDebugNotification(
            `🔄 Tentativa ${attempt + 2}/${MAX_RETRIES} de upload...`,
            "info",
          );
        }
      }
    }

    attachment.isUploading = false;
    attachment.uploadProgress = 0;
    attachment.uploadError =
      lastError?.message || t("chatjess.errors.uploadFailed");

    if (fileCategory === "image") {
      try {
        const base64Data = await fileToBase64(processedFile);
        attachment.url = base64Data;
        attachment.uploadProgress = 100;
        attachment.isLocalOnly = true;

        logger.warn(
          "Upload failed after retries, using base64 fallback for image",
        );
        showDebugNotification(
          "⚠️ Upload falhou após tentativas, usando cópia local. A Jess não poderá processar esta imagem.",
          "warning",
        );
        return attachment;
      } catch (base64Error) {
        logger.error("Failed to create base64 fallback:", base64Error);
      }
    }

    const errorMessage =
      lastError?.message || t("chatjess.errors.unknownError");
    if (
      errorMessage.includes("413") ||
      errorMessage.includes("Content Too Large")
    ) {
      throw new Error(t("chatjess.errors.fileTooLargeDetailed"));
    } else if (
      errorMessage.includes("CORS") ||
      errorMessage.includes("blocked")
    ) {
      throw new Error(t("chatjess.errors.corsError"));
    } else {
      throw new Error(
        t("chatjess.errors.uploadFailedWithRetries", {
          retries: MAX_RETRIES,
          error: errorMessage,
        }),
      );
    }
  };

  // Função para aplicar tema na sidebar
  const applySidebarTheme = useCallback((isDark: boolean) => {
    // SEMPRE remover CSS anterior primeiro
    const existingStyle = document.getElementById("chatjess-sidebar-theme");
    if (existingStyle) {
      existingStyle.remove();
    }

    // Definir cores baseadas no tema
    const newBgColor = isDark
      ? "rgba(30, 30, 30, 0.98)"
      : "linear-gradient(180deg, #93F090 0%, #60BFB7 100%)";
    const newBorderColor = isDark
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(177, 224, 215, 0.5)";

    // Criar e injetar CSS com máxima prioridade
    const style = document.createElement("style");
    style.id = "chatjess-sidebar-theme";
    style.textContent = `
      /* FORÇAR tema na sidebar - ${isDark ? "ESCURO" : "CLARO"} */
      
      /* Seletores específicos apenas para a sidebar */
      div[style*="width: 4.6875rem"][style*="minHeight: 100vh"],
      div[style*="width: 75px"][style*="minHeight: 100vh"],
      div[style*="width: 4.6875rem"][style*="min-height: 100vh"],
      div[style*="width: 75px"][style*="min-height: 100vh"] {
        background: ${newBgColor} !important;
        border-right: 1px solid ${newBorderColor} !important;
      }
    `;

    document.head.appendChild(style);

    // Buscar e aplicar estilos inline como backup
    const searchAndApply = (attempt = 1) => {
      if (attempt > 10) {
        return;
      }

      // Estratégia 1: Buscar pelo wrapper principal
      const layoutWrapper = document.querySelector(".layout-wrapper");
      if (layoutWrapper) {
        const firstChild = layoutWrapper.firstElementChild as HTMLElement;
        if (firstChild) {
          firstChild.style.setProperty("background", newBgColor, "important");
          firstChild.style.setProperty(
            "border-right",
            `1px solid ${newBorderColor}`,
            "important",
          );
          return;
        }
      }

      // Estratégia 2: Buscar por todas as divs com largura específica
      const allDivs = document.querySelectorAll("div");
      for (const div of allDivs) {
        const computedStyle = window.getComputedStyle(div);
        const inlineStyle = (div as HTMLElement).style.cssText;

        // Verificar se tem características da sidebar
        const hasCorrectWidth =
          computedStyle.width === "75px" ||
          computedStyle.width.includes("4.6875rem") ||
          inlineStyle.includes("width: 4.6875rem") ||
          inlineStyle.includes("width: 75px");

        const hasCorrectHeight =
          computedStyle.minHeight === "100vh" ||
          inlineStyle.includes("minHeight: 100vh");

        const hasCorrectBg =
          computedStyle.backgroundColor === "rgb(54, 39, 111)" ||
          computedStyle.backgroundColor === "rgba(30, 30, 30, 0.98)" ||
          computedStyle.backgroundColor === "rgb(30, 30, 30)" ||
          inlineStyle.includes("#36276F") ||
          inlineStyle.includes("rgb(54, 39, 111)") ||
          inlineStyle.includes("rgba(30, 30, 30") ||
          inlineStyle.includes("rgb(30, 30, 30)");

        if (
          (hasCorrectWidth && hasCorrectHeight) ||
          (hasCorrectWidth && hasCorrectBg) ||
          hasCorrectBg
        ) {
          (div as HTMLElement).style.setProperty(
            "background",
            newBgColor,
            "important",
          );
          (div as HTMLElement).style.setProperty(
            "border-right",
            `1px solid ${newBorderColor}`,
            "important",
          );
          return;
        }
      }

      // Se não encontrou, tentar novamente
      setTimeout(() => searchAndApply(attempt + 1), 100 * attempt);
    };

    // Iniciar busca com delay pequeno
    setTimeout(() => searchAndApply(), 50);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const isDark = (e as CustomEvent).detail.isDark;
      setIsDarkTheme(isDark);
      applySidebarTheme(isDark);
    };
    window.addEventListener("appThemeChange", handler);
    return () => window.removeEventListener("appThemeChange", handler);
  }, []);

  useEffect(() => {
    // Tema local apenas para Chat Jess - não afetar outras páginas
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      const isDark = savedTheme === "dark";
      setIsDarkTheme(isDark);
    } else {
      // Padrão é modo claro
      setIsDarkTheme(false);
      localStorage.setItem("theme", "light");
    }

    // Atualizar estado da sidebar com base no breakpoint
    const saved = localStorage.getItem("sidebar");

    // Lógica responsiva para sidebar
    if (breakpointInfo.isMobile || breakpointInfo.isTablet) {
      // Em mobile/tablet: sempre começar recolhido (independente do estado salvo)
      setIsSidebarCollapsed(true);
    } else {
      // Em desktop: usar estado salvo ou comportamento padrão
      setIsSidebarCollapsed(saved === "collapsed");
    }

    // Atualiza o display da sidebar real baseado no estado
    // Esta lógica precisa ser robusta e talvez revisada
    setTimeout(() => {
      const layoutWrapper = document.querySelector(".layout-wrapper");
      if (layoutWrapper) {
        const sideMenuElement = findSidebarElement(layoutWrapper);
        if (sideMenuElement) {
          // Determina se deve estar oculta baseado no estado atualizado
          const shouldBeHidden =
            breakpointInfo.isMobile || breakpointInfo.isTablet
              ? saved !== "expanded"
              : saved === "collapsed";
          (sideMenuElement as HTMLElement).style.display = shouldBeHidden
            ? "none"
            : "";
        }
      }
    }, 100);

    // Aplicar tema na sidebar após carregar
    setTimeout(() => {
      const currentTheme = localStorage.getItem("theme") || "light";
      applySidebarTheme(currentTheme === "dark");
    }, 100);

    // Cleanup: Quando sair da página, remover tema global e resetar sidebar
    return () => {
      // Remover classe do body (caso tenha sido aplicada)
      document.body.classList.remove("lightTheme");

      // Remover CSS injetado
      const existingStyle = document.getElementById("chatjess-sidebar-theme");
      if (existingStyle) {
        existingStyle.remove();
      }

      // Resetar sidebar para cor padrão
      setTimeout(() => {
        const layoutWrapper = document.querySelector(".layout-wrapper");
        if (layoutWrapper) {
          const firstChild = layoutWrapper.firstElementChild as HTMLElement;
          if (firstChild) {
            firstChild.style.removeProperty("background-color");
            firstChild.style.removeProperty("background");
            firstChild.style.removeProperty("border-right");
          }
        }

        // Buscar por todas as divs da sidebar e resetar
        const allDivs = document.querySelectorAll("div");
        for (const div of allDivs) {
          const computedStyle = window.getComputedStyle(div);
          const hasCorrectWidth =
            computedStyle.width === "75px" ||
            computedStyle.width.includes("4.6875rem");

          if (hasCorrectWidth) {
            (div as HTMLElement).style.removeProperty("background-color");
            (div as HTMLElement).style.removeProperty("background");
            (div as HTMLElement).style.removeProperty("border-right");
          }
        }
      }, 50);
    };
  }, [applySidebarTheme, breakpointInfo.isMobile, breakpointInfo.isTablet]);

  const findSidebarElement = (layoutWrapper: Element) => {
    const firstChild = layoutWrapper.firstElementChild;
    return firstChild;
  };

  const handleSend = () => {
    const hasMessage = currentMessage.trim() !== "";
    const hasAttachments = pendingAttachments.length > 0;

    if (!hasMessage && !hasAttachments) return;

    // Check if any attachments are still uploading
    const hasUploadingAttachments = pendingAttachments.some(
      (att) => att.isUploading,
    );
    if (hasUploadingAttachments) {
      showDebugNotification(
        "⏳ Aguarde o upload dos arquivos terminar antes de enviar",
        "info",
      );
      return;
    }

    // Check if any attachments failed to upload
    const hasFailedUploads = pendingAttachments.some(
      (att) => att.uploadError || (!att.url && !att.isUploading),
    );
    if (hasFailedUploads) {
      showDebugNotification(
        "❌ Alguns arquivos falharam no upload. Remova-os ou tente novamente.",
        "error",
      );
      return;
    }

    // Check for local-only files that cannot be processed by the agent
    const hasLocalOnlyFiles = pendingAttachments.some(
      (att) => att.isLocalOnly || att.url?.startsWith("blob:"),
    );
    if (hasLocalOnlyFiles) {
      showDebugNotification(
        "⚠️ Alguns arquivos estão apenas locais. A Jess não poderá processá-los.",
        "warning",
      );
    }

    // Mark that user has sent a message
    setHasUserSentMessage(true);

    // Check if agent is offline and show notification
    if (agentConnectionStatus && !agentConnectionStatus.isAgentConnected) {
      setShowOfflineNotification(true);
      setTimeout(() => {
        setShowOfflineNotification(false);
      }, 4000);
    }

    // If we have multiple attachments, send one message per attachment
    if (hasAttachments && pendingAttachments.length > 1) {
      // Send one message for each attachment
      pendingAttachments.forEach((attachment, index) => {
        const messageType: "file" | "mixed" = hasMessage ? "mixed" : "file";

        const newMessage: ChatJessMessage = {
          id: Date.now().toString() + "_" + index,
          content: currentMessage || "",
          timestamp: new Date(),
          author: "human",
          feedbackId: undefined,
          feedbackTitle: undefined,
          feedbackDetails: undefined,
          attachments: [attachment], // One attachment per message
          messageType,
        };

        wsSendMessage(newMessage);
      });
    } else {
      // Single attachment or no attachments - send as before
      const messageType: "text" | "file" | "mixed" =
        hasMessage && hasAttachments
          ? "mixed"
          : hasAttachments
            ? "file"
            : "text";

      const newMessage: ChatJessMessage = {
        id: Date.now().toString(),
        content: currentMessage || "",
        timestamp: new Date(),
        author: "human",
        feedbackId: undefined,
        feedbackTitle: undefined,
        feedbackDetails: undefined,
        attachments:
          pendingAttachments.length > 0 ? [...pendingAttachments] : undefined,
        messageType,
      };

      wsSendMessage(newMessage);
    }

    setCurrentMessage("");
    setPendingAttachments([]); // Clear pending attachments

    setTimeout(scrollToBottom, 100);
  };

  const handleFeedbackClick = (msg: ChatJessMessage) => {
    setFeedbackMsg(msg);
    setActiveTab("report");
    setFeedbackOpen(true);
    // On mobile, close KPI panel to avoid full-screen overlap
    if (breakpointInfo.isMobile || breakpointInfo.isTablet) {
      setKpiPanelOpen(false);
    }
  };

  // Função para mostrar notificações de debug (definida antes de ser usada)
  const showDebugNotification = useCallback(
    (
      message: string,
      type: "success" | "info" | "warning" | "error" = "info",
    ) => {
      setDebugNotification({ show: true, message, type });
      setTimeout(
        () => {
          setDebugNotification((prev) => ({ ...prev, show: false }));
        },
        type === "error" ? 5000 : 3000,
      );
    },
    [],
  );

  // Função para fechar painel de feedback
  const closeFeedbackPanel = useCallback(() => {
    setFeedbackOpen(false);
    setSuggestionText("");
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDarkTheme;
    setIsDarkTheme(newTheme);
    localStorage.setItem("theme", newTheme ? "dark" : "light");

    // Aplicar tema na sidebar
    setTimeout(() => {
      applySidebarTheme(newTheme);
    }, 50);

    // Disparar evento para componentes que precisam detectar mudança de tema
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("appThemeChange", { detail: { isDark: newTheme } }),
      );
    }, 100);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setCurrentMessage(value);

    // Nova verificação - permitir comandos em qualquer ambiente
    // Comentando a variável não utilizada
    // const isLocalhost = true; // Forçar como verdadeiro para permitir comandos em qualquer lugar

    if (value === "/debug-on") {
      logger.enableDebug();
      showDebugNotification(
        "🐛 Modo DEBUG ativado! Logs aparecerão no console.",
        "success",
      );
      setCurrentMessage("");
      return;
    }

    if (value === "/debug-off") {
      logger.disableDebug();
      showDebugNotification(
        "🔇 Modo DEBUG desativado! Logs não aparecerão no console.",
        "info",
      );
      setCurrentMessage("");
      return;
    }

    if (value === "/mock-on") {
      // Ativar modo mock dinamicamente
      localStorage.setItem("CHATJESS_MOCK_MODE", "true");
      showDebugNotification(
        "🎭 Modo MOCK ativado! Recarregue a página para aplicar.",
        "success",
      );
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      setCurrentMessage("");
      return;
    }

    if (value === "/mock-off") {
      // Desativar modo mock dinamicamente
      localStorage.setItem("CHATJESS_MOCK_MODE", "false");
      showDebugNotification(
        "🌐 Modo REAL ativado! Recarregue a página para aplicar.",
        "info",
      );
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      setCurrentMessage("");
      return;
    }

    if (value === "/ws-status") {
      const status = getWSStatusText();
      showDebugNotification(status, "info");
      setCurrentMessage("");
      return;
    }

    if (value === "/ws-reconnect") {
      showDebugNotification("🔄 Tentando reconectar WebSocket...", "info");
      reconnect();
      setCurrentMessage("");
      return;
    }

    if (value === "/help") {
      const helpMessage = `📋 Comandos Disponíveis (todos os ambientes):
      /debug-on - Ativar logs de debug
      /debug-off - Desativar logs de debug
      /mock-on - Ativar modo mock (recarrega página)
      /mock-off - Ativar modo real (recarrega página)
      /ws-status - Status do WebSocket e modo atual
      /ws-reconnect - Reconectar WebSocket
      /help - Mostrar esta ajuda`;
      showDebugNotification(helpMessage, "info");
      setCurrentMessage("");
      return;
    }

    // Auto-resize
    e.target.style.height = "";
    const newHeight = Math.min(e.target.scrollHeight, 200);
    e.target.style.height = newHeight + "px";

    // Reposicionar modal de anexos IMEDIATAMENTE durante a digitação
    const repositionModal = () => {
      const modal = document.querySelector(
        `.${styles.pendingAttachmentsModal}`,
      ) as HTMLElement;
      const inputForm = document.querySelector(
        `.${styles.inputForm}`,
      ) as HTMLElement;

      if (modal && inputForm && pendingAttachments.length > 0) {
        // Usar getBoundingClientRect para altura precisa em tempo real
        const inputFormRect = inputForm.getBoundingClientRect();
        const inputFormHeight = inputFormRect.height;

        // Posicionar modal 25px acima da barra de input (antes era 15px)
        const modalBottomPosition = inputFormHeight + 30;

        modal.style.bottom = `${modalBottomPosition}px`;
      }
    };

    // Executar reposicionamento imediatamente e após o próximo frame
    repositionModal();
    requestAnimationFrame(repositionModal);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;
    const files: { file: File; category: "document" | "image" | "audio" }[] =
      [];

    // First pass: collect all files
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.kind === "file") {
        const file = item.getAsFile();

        if (file) {
          // Determine file category based on extension
          const fileName = file.name.toLowerCase();
          const extension = fileName.substring(fileName.lastIndexOf("."));

          // Check if this is an allowed file type
          if (!Object.hasOwn(ALLOWED_FILE_TYPES, extension)) {
            continue; // Skip unsupported files during paste
          }

          let fileCategory: "document" | "image" | "audio" = "document";

          // Determine category based on extension
          if ([".jpg", ".jpeg", ".png", ".gif", ".webp"].includes(extension)) {
            fileCategory = "image";
          } else if ([".pdf", ".txt", ".csv"].includes(extension)) {
            fileCategory = "document";
          } else {
            // For audio files, we're not supporting them via paste in this implementation
            continue;
          }

          files.push({ file, category: fileCategory });
        }
      }
    }

    // If files were detected, prevent the default paste behavior
    if (files.length > 0) {
      e.preventDefault();
      e.stopPropagation();

      // Use the batch function for multiple files
      await addMultipleAttachments(files);
    }
  };

  // Handler para submeter resposta no novo formato de feedback (text and/or attachments)
  const handleSubmitAnswer = useCallback(
    async (
      feedbackId: string,
      answer: string,
      questionIndex: number = 0,
      attachments?: FileAttachment[],
      fileMap?: { attachmentId: string; file: File }[],
    ): Promise<boolean> => {
      const hasText = answer.trim().length > 0;
      const hasAttachments = attachments && attachments.length > 0;
      if (!feedbackId || (!hasText && !hasAttachments)) return false;

      let attachmentsToSend: FileAttachment[] | undefined;

      if (fileMap?.length && attachments?.length) {
        const fileMapById = new Map(
          fileMap.map(({ attachmentId, file }) => [attachmentId, file]),
        );
        const uploadFailures: { attachmentId: string; error: unknown }[] = [];
        attachmentsToSend = [];

        for (const att of attachments) {
          const file = fileMapById.get(att.id);
          if (file) {
            try {
              const uploadResult = await uploadFile(file);
              attachmentsToSend.push({
                ...att,
                url: uploadResult.url,
                uploadError: undefined,
              });
            } catch (err) {
              logger.warn("Feedback attachment upload failed:", err);
              uploadFailures.push({ attachmentId: att.id, error: err });
            }
          } else {
            attachmentsToSend.push({ ...att });
          }
        }

        if (uploadFailures.length > 0) {
          showDebugNotification(t("chatjess.errors.uploadFailed"), "error");
          return false;
        }
      } else {
        attachmentsToSend = attachments ? [...attachments] : undefined;
      }

      const messageToUpdate = wsMessages.find(
        (msg) => msg.feedbackId === feedbackId,
      );

      if (messageToUpdate && messageToUpdate.supportMessages) {
        const updatedSupportMessages = [...messageToUpdate.supportMessages];
        if (updatedSupportMessages[questionIndex]) {
          updatedSupportMessages[questionIndex].answer = answer;
        }

        const allAnswered = updatedSupportMessages.every((q) => q.answer);

        if (allAnswered) {
          showDebugNotification(
            "✅ Todas as perguntas foram respondidas! Fechando painel...",
            "success",
          );

          setTimeout(() => {
            closeFeedbackPanel();
          }, 1500);
        }
      }

      sendFeedbackResponse(
        feedbackId,
        answer,
        messageToUpdate,
        attachmentsToSend,
        questionIndex,
      );

      setSuggestionText("");
      showDebugNotification("✅ Resposta enviada para Jess!", "success");
      return true;
    },
    [
      userProfile?.userName,
      wsMessages,
      sendFeedbackResponse,
      closeFeedbackPanel,
      setSuggestionText,
      showDebugNotification,
      uploadFile,
      t,
    ],
  );

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setDetailModalTitle("");
    setDetailModalContent("");
  };

  // Funções do emoji picker
  const handleEmojiButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    setShowEmojiPicker((prev) => !prev);
  };

  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    setCurrentMessage((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);

    // Focar no input após adicionar emoji
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Funções do modal de anexos
  const handleAttachmentButtonClick = () => {
    setShowAttachmentModal((prev) => !prev);
  };

  const handleAttachmentSelect = (type: "document" | "image" | "audio") => {
    // Criar input de arquivo dinamicamente
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true; // Permitir seleção múltipla

    if (type === "document") {
      input.accept = ".pdf,.txt,.csv";
    } else if (type === "image") {
      input.accept = ".jpg,.jpeg,.png,.gif,.webp";
    } else if (type === "audio") {
      input.accept = ".mp3,.mp4,.webm,.ogg,.wav";
    }

    input.onchange = async (e) => {
      const selectedFiles = (e.target as HTMLInputElement).files;
      if (selectedFiles && selectedFiles.length > 0) {
        // Convert FileList to array with categories
        const files: {
          file: File;
          category: "document" | "image" | "audio";
        }[] = [];
        for (let i = 0; i < selectedFiles.length; i++) {
          files.push({ file: selectedFiles[i], category: type });
        }

        // Use batch function for multiple files
        await addMultipleAttachments(files);
      }
    };

    input.click();
    setShowAttachmentModal(false);
  };

  // Effect para fechar emoji picker ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Verificar se o clique foi fora do picker ou botão
      if (
        !target.closest('[data-testid="emoji-picker"]') &&
        !target.closest(".emoji-button") &&
        !target.closest(".emoji-picker-container")
      ) {
        setShowEmojiPicker(false);
      }

      // Verificar se o clique foi fora do modal de anexos ou botão
      if (
        !target.closest(".attachment-modal") &&
        !target.closest(".attachment-button")
      ) {
        setShowAttachmentModal(false);
      }

      // Verificar se o clique foi fora do modal de contexto da imagem
      if (
        !target.closest(".image-context-modal") &&
        !target.closest(".image-context-button")
      ) {
        closeImageContextModal();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Effect para monitorar mudanças na altura da barra de input e reposicionar modal
  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;

    const updateModalPosition = () => {
      const modal = document.querySelector(
        `.${styles.pendingAttachmentsModal}`,
      ) as HTMLElement;
      const inputForm = document.querySelector(
        `.${styles.inputForm}`,
      ) as HTMLElement;

      if (modal && inputForm && pendingAttachments.length > 0) {
        const inputFormRect = inputForm.getBoundingClientRect();
        const inputFormHeight = inputFormRect.height;

        // Posicionar modal 25px acima da barra de input (antes era 15px)
        const modalBottomPosition = inputFormHeight + 25;

        modal.style.bottom = `${modalBottomPosition}px`;
      }
    };

    // Configurar observer apenas se há anexos pendentes
    if (pendingAttachments.length > 0) {
      // Posição inicial
      setTimeout(updateModalPosition, 100);

      // Observer para mudanças na altura do formulário
      const inputForm = document.querySelector(`.${styles.inputForm}`);
      if (inputForm) {
        resizeObserver = new ResizeObserver(() => {
          updateModalPosition();
        });
        resizeObserver.observe(inputForm);
      }

      // Monitor redimensionamento da janela
      window.addEventListener("resize", updateModalPosition);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", updateModalPosition);
    };
  }, [pendingAttachments.length]);

  // Function to add multiple attachments at once
  const addMultipleAttachments = async (
    filesWithCategories: {
      file: File;
      category: "document" | "image" | "audio";
    }[],
  ) => {
    // Create temporary attachments for all files
    const tempAttachments: FileAttachment[] = filesWithCategories.map(
      ({ file }) => ({
        id: Date.now().toString() + Math.random().toString(36).substring(2, 11),
        name: file.name,
        size: file.size,
        type: file.type,
        thumbnail: "",
        isUploading: true,
        uploadProgress: 0,
      }),
    );

    // Add all temporary attachments at once
    setPendingAttachments((prev) => [...prev, ...tempAttachments]);

    // Process all files in parallel
    const processingPromises = filesWithCategories.map(
      async ({ file, category }, index) => {
        try {
          const attachment = await processFile(file, category);
          return {
            success: true,
            attachment,
            tempId: tempAttachments[index].id,
          };
        } catch (error) {
          logger.error("Erro ao processar arquivo:", error);
          return { success: false, tempId: tempAttachments[index].id, error };
        }
      },
    );

    const results = await Promise.all(processingPromises);

    // Update all attachments at once
    setPendingAttachments((prev) => {
      let updated = [...prev];

      results.forEach((result) => {
        if (result.success && result.attachment) {
          // Update successful uploads
          updated = updated.map((att) =>
            att.id === result.tempId ? result.attachment! : att,
          );
        } else {
          // Remove failed uploads
          updated = updated.filter((att) => att.id !== result.tempId);
          if (result.error) {
            showDebugNotification(
              `❌ Erro: ${(result.error as Error).message}`,
              "info",
            );
          }
        }
      });

      return updated;
    });
  };

  const removeAttachment = (attachmentId: string) => {
    setPendingAttachments((prev) => {
      const attachment = prev.find((a) => a.id === attachmentId);
      // Only revoke object URLs (blob URLs), not server URLs
      if (attachment?.url && attachment.url.startsWith("blob:")) {
        URL.revokeObjectURL(attachment.url);
      }
      return prev.filter((a) => a.id !== attachmentId);
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only show drag overlay if files are being dragged
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only hide overlay if we're leaving the main container
    // Check if the related target is outside the drop zone
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length === 0) return;

    // Collect all files with their categories
    const files: { file: File; category: "document" | "image" | "audio" }[] =
      [];

    for (let i = 0; i < droppedFiles.length; i++) {
      const file = droppedFiles[i];
      let fileCategory: "document" | "image" | "audio" = "document";

      // Determine file category based on extension
      const fileName = file.name.toLowerCase();
      const extension = fileName.substring(fileName.lastIndexOf("."));

      // Check if this is an allowed file type
      if (!Object.hasOwn(ALLOWED_FILE_TYPES, extension)) {
        showDebugNotification(
          `❌ ${t("chatjess.attachments.unsupportedFileType", {
            extension: extension || t("chatjess.attachments.unknownExtension"),
            allowedTypes: getAllowedExtensions(),
          })}`,
          "info",
        );
        continue; // Skip unsupported files during drop
      }

      // Determine category based on extension
      if ([".jpg", ".jpeg", ".png", ".gif", ".webp"].includes(extension)) {
        fileCategory = "image";
      } else if ([".pdf", ".txt", ".csv"].includes(extension)) {
        fileCategory = "document";
      } else if (
        [".mpeg", ".mp3", ".ogg", ".wav", ".webm", ".mp4"].includes(extension)
      ) {
        fileCategory = "audio";
      } else {
        // This shouldn't happen if ALLOWED_FILE_TYPES is correctly configured
        logger.error(
          `Unexpected file extension in allowed types: ${extension}`,
        );
        continue;
      }

      files.push({ file, category: fileCategory });
    }

    // Process all files at once
    await addMultipleAttachments(files);
  };

  const closeImageContextModal = () => {
    setImageContextModal({
      show: false,
      attachmentId: "",
      position: { x: 0, y: 0 },
    });
  };

  // Function to attempt chat history loading with retries
  const attemptChatHistoryLoad = useCallback(() => {
    const authData = localStorage.getItem("authUser");
    if (!authData) {
      return false;
    }

    try {
      const parsedAuth = JSON.parse(authData);
      const telephone = parsedAuth.telephone || "";

      if (!telephone) {
        return false;
      }

      const clientID = `${telephone}@c.us`;

      // Send the chatConnected request
      sendChatConnected(clientID, "staff");

      return true;
    } catch (error) {
      logger.error("[ChatJess] Error parsing auth data:", error);
      return false;
    }
  }, [sendChatConnected]);

  // Function to start the retry mechanism
  const startChatHistoryRetryMechanism = useCallback(() => {
    // Reset states
    setIsLoadingChatHistory(true);
    setChatHistoryTimeout(false);
    setChatHistoryRetryCount(0);

    // Clear any existing timers
    if (chatHistoryTimeoutRef.current) {
      clearTimeout(chatHistoryTimeoutRef.current);
    }
    if (chatHistoryRetryIntervalRef.current) {
      clearInterval(chatHistoryRetryIntervalRef.current);
    }

    // First attempt immediately (retryCount stays 0 to show "loadingHistory" message)
    if (!attemptChatHistoryLoad()) {
      logger.error("[ChatJess] Failed to make first attempt, aborting");
      setIsLoadingChatHistory(false);
      return;
    }

    // Set up retry interval
    let retryCount = 0;
    chatHistoryRetryIntervalRef.current = setInterval(() => {
      // Check if we're still in the right conditions
      if (!agentConnectionStatus?.isAgentConnected || !isConnected) {
        if (chatHistoryRetryIntervalRef.current) {
          clearInterval(chatHistoryRetryIntervalRef.current);
          chatHistoryRetryIntervalRef.current = null;
        }

        setIsLoadingChatHistory(false);
        return;
      }

      retryCount++;
      setChatHistoryRetryCount(retryCount);
      attemptChatHistoryLoad();

      if (retryCount >= maxRetryAttempts) {
        if (chatHistoryRetryIntervalRef.current) {
          clearInterval(chatHistoryRetryIntervalRef.current);
          chatHistoryRetryIntervalRef.current = null;
        }

        setIsLoadingChatHistory(false);
        setChatHistoryTimeout(true);
      }
    }, retryIntervalMs);
  }, [
    attemptChatHistoryLoad,
    agentConnectionStatus?.isAgentConnected,
    isConnected,
  ]);

  const handleDownloadImage = async (attachment: FileAttachment) => {
    if (!attachment.url) return;

    try {
      if (isAgentProxyUrl(attachment.url)) {
        // Use special download for agent-proxy URLs
        await downloadFile(attachment.url, attachment.name);
      } else if (attachment.type.startsWith("image/")) {
        // Para imagens, abrir em nova aba
        window.open(attachment.url, "_blank");
      } else {
        // Para documentos, baixar
        const link = document.createElement("a");
        link.href = attachment.url;
        link.download = attachment.name;
        link.click();
      }
    } catch (error) {
      logger.error("Erro ao baixar arquivo:", error);
      showDebugNotification(
        `❌ Erro ao baixar arquivo: ${(error as Error).message}`,
        "info",
      );
    }

    closeImageContextModal();
  };

  useEffect(() => {
    const newDisplayMessages = wsMessages;

    setDisplayMessages((prevDisplayMessages) => {
      const lengthChanged =
        prevDisplayMessages.length !== newDisplayMessages.length;

      if (lengthChanged) {
        setTimeout(scrollToBottom, 100);
        return newDisplayMessages;
      }

      const contentChanged = prevDisplayMessages.some((prevMsg, i) => {
        const newMsg = newDisplayMessages[i];
        return (
          prevMsg.id !== newMsg?.id ||
          prevMsg.content !== newMsg?.content ||
          prevMsg.supportMessages?.length !== newMsg?.supportMessages?.length
        );
      });

      if (contentChanged) {
        if (feedbackMsg?.feedbackId) {
          const currFeedbackMsg = newDisplayMessages.find(
            (msg) =>
              msg.feedbackId && msg.feedbackId === feedbackMsg?.feedbackId,
          );
          setFeedbackMsg(currFeedbackMsg || null);
        }

        return newDisplayMessages;
      }

      return prevDisplayMessages;
    });
  }, [wsMessages]);

  // Determine if offline indicator should be shown
  const shouldShowOfflineIndicator = () => {
    // This function will only be called after agent status is loaded
    // Don't show offline indicator for new users (they'll see welcome animation instead)
    if (isNewUser()) return false;

    // Show if agent is offline AND user hasn't sent any message in this session yet
    if (!agentConnectionStatus) return false;

    return (
      !agentConnectionStatus.isAgentConnected &&
      !hasUserSentMessage &&
      !hasClosedOfflineIndicator
    );
  };

  // Function to check agent connection status
  const checkAgentConnectionStatus = useCallback(async () => {
    // Add a ref to track if component is mounted
    let isMounted = true;

    try {
      const status = await fetchAgentConnectionStatus();
      if (isMounted) {
        setAgentConnectionStatus(status);
      }
    } catch (error) {
      logger.error("Error checking agent connection status:", error);

      if (isMounted) {
        // On error, set default offline status so we don't stay in loading forever
        const fallbackStatus = {
          companyId: "unknown",
          isAgentConnected: false,
          timestamp: Date.now(),
        };
        setAgentConnectionStatus(fallbackStatus);
      }
    } finally {
      if (isMounted) {
        setIsLoadingAgentStatus(false);
      }
    }

    // Cleanup function to mark component as unmounted
    return () => {
      isMounted = false;
    };
  }, []);

  // Set up polling for agent connection status
  useEffect(() => {
    // Check immediately on mount
    checkAgentConnectionStatus();

    // Set up interval for polling every 30 seconds
    const intervalId = setInterval(() => {
      checkAgentConnectionStatus();
    }, 30000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [checkAgentConnectionStatus]);

  // Effect to send chatConnected when agent becomes connected
  useEffect(() => {
    // Check if agent is connected, WebSocket is connected, and history hasn't been loaded yet
    if (
      agentConnectionStatus?.isAgentConnected &&
      isConnected &&
      !chatHistoryLoadedRef.current
    ) {
      // Start the retry mechanism
      startChatHistoryRetryMechanism();

      // Mark history as loaded to prevent duplicate requests
      chatHistoryLoadedRef.current = true;
    }

    // Cleanup function
    return () => {
      if (chatHistoryTimeoutRef.current) {
        clearTimeout(chatHistoryTimeoutRef.current);
        chatHistoryTimeoutRef.current = null;
      }
      if (chatHistoryRetryIntervalRef.current) {
        clearInterval(chatHistoryRetryIntervalRef.current);
        chatHistoryRetryIntervalRef.current = null;
      }
    };
  }, [
    agentConnectionStatus?.isAgentConnected,
    isConnected,
    startChatHistoryRetryMechanism,
  ]);

  // Auto-stop loading when messages are received
  useEffect(() => {
    if (isLoadingChatHistory && displayMessages.length > 0) {
      setIsLoadingChatHistory(false);
      setChatHistoryTimeout(false);

      // Clear all timers
      if (chatHistoryTimeoutRef.current) {
        clearTimeout(chatHistoryTimeoutRef.current);
        chatHistoryTimeoutRef.current = null;
      }
      if (chatHistoryRetryIntervalRef.current) {
        clearInterval(chatHistoryRetryIntervalRef.current);
        chatHistoryRetryIntervalRef.current = null;
      }
    }
  }, [isLoadingChatHistory, displayMessages.length]);

  // Classes CSS dinâmicas
  const mainContentClass = `${styles.mainContent} ${isSidebarCollapsed ? styles.expanded : ""}`;
  const inputClass = `${styles.messageInputContainer} ${isSidebarCollapsed ? styles.expanded : ""}`;
  const feedbackPanelClass = `${styles.feedbackPanel} ${feedbackOpen ? styles.open : ""} ${isSidebarCollapsed ? styles.sidebarCollapsed : ""}`;

  // Adicione os estados para controlar a gravação de áudio
  const [audioRecorderState, setAudioRecorderState] = useState<
    "idle" | "recording" | "recorded"
  >("idle");

  // Função para lidar com o clique no botão de microfone
  const handleMicrophoneClick = () => {
    setAudioRecorderState("recording");
  };

  // Função para cancelar a gravação de áudio
  const handleCancelRecording = useCallback(() => {
    setAudioRecorderState("idle");
    // Garantir que o input volta ao foco
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  }, []);

  // Função para processar o áudio gravado
  const handleAudioRecorded = async (
    audioBlob: Blob,
    waveformData?: number[],
    duration?: number,
  ) => {
    try {
      showDebugNotification("⏳ Enviando áudio...", "info");

      // Create a File object from the Blob for upload
      const fileName = `audio_${Date.now()}.${audioBlob.type === "audio/mp4" ? "mp4" : "webm"}`;
      const audioFile = new File([audioBlob], fileName, {
        type: audioBlob.type || "audio/webm",
        lastModified: Date.now(),
      });

      // Upload the audio file using the same uploadFile API
      const uploadResult = await uploadFile(audioFile);

      // Create attachment object similar to other file attachments
      const audioAttachment: FileAttachment = {
        id: Date.now().toString() + Math.random().toString(36).substring(2, 11),
        name: fileName,
        size: audioFile.size,
        type: audioFile.type,
        url: uploadResult.url,
        uploadProgress: 100,
        isUploading: false,
      };

      // Create message with audio attachment
      const audioMessage: ChatJessMessage = {
        id: Date.now().toString(),
        content: "", // Audio messages typically don't have text content
        author: "human",
        timestamp: new Date(),
        attachments: [audioAttachment],
        messageType: "file", // Use "file" type since it has attachments
        // Keep audio data for local playback UI if needed
        audioData: {
          url: uploadResult.url,
          blob: audioBlob,
          base64: "", // Not needed for upload-based approach
          duration: duration || audioBlob.size / (64000 / 8), // Estimated duration
          mimeType: audioBlob.type || "audio/webm",
          waveformData:
            waveformData && waveformData.length > 0 ? waveformData : undefined,
        },
        isAudioMessage: true,
      };

      wsSendMessage(audioMessage);

      // Reset states
      setAudioRecorderState("idle");

      setTimeout(scrollToBottom, 100);
    } catch (error) {
      logger.error("Erro ao processar/enviar o áudio:", error);
      showDebugNotification("❌ Erro ao enviar áudio", "info");
      handleCancelRecording();
    }
  };

  // Renderização condicional do botão de enviar ou microfone com animação
  const renderSendButton = () => {
    return (
      <AnimatePresence mode="wait">
        {currentMessage.trim() || pendingAttachments.length > 0 ? (
          <motion.button
            key="send"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={{ duration: 0.2 }}
            id="jess-send-button"
            className={styles.sendButton}
            onClick={handleSend}
            title={t("chatjess.ui.sendMessage")}
          >
            <i className="fas fa-paper-plane" />
          </motion.button>
        ) : (
          <motion.button
            key="mic"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={{ duration: 0.2 }}
            id="jess-microphone-button"
            className={styles.micButton}
            onClick={handleMicrophoneClick}
            title={t("chatjess.ui.recordAudio")}
          >
            <i className="fas fa-microphone" />
          </motion.button>
        )}
      </AnimatePresence>
    );
  };

  // Função para determinar se deve mostrar botões de anexo e emoji
  const shouldShowAttachmentAndEmojiButtons = () => {
    return audioRecorderState === "idle";
  };

  // Função para obter status formatado do WebSocket
  const getWSStatusText = () => {
    const currentMockMode =
      process.env.REACT_APP_CHATJESS_USE_MOCK === "true" ||
      localStorage.getItem("CHATJESS_MOCK_MODE") === "true";

    return `🔌 WebSocket Status:
    - Modo: ${currentMockMode ? "🎭 MOCK" : "🌐 REAL"}
    - Conectado: ${isConnected ? "✅" : "❌"}
    - Conectando: ${connection.isConnecting ? "⏳" : "✅"}
    - Erro: ${connection.error || "Nenhum"}
    - Último ping: ${connection.lastPing?.toLocaleTimeString() || "N/A"}
    - Ambiente: ${window.location.hostname}`;
  };

  return (
    <div className={pageWrapperClass}>
      {/* Modal Backdrop */}
      <div
        className={`${styles.modalBackdrop} ${feedbackOpen ? styles.show : ""}`}
      />

      {/* Debug Notification */}
      <div
        className={`${styles.debugNotification} ${debugNotification.show ? styles.show : ""} ${styles[debugNotification.type]}`}
      >
        {debugNotification.message}
      </div>

      {/* Main Content */}
      <div className={mainContentClass} ref={mainContentRef}>
        {/* Responsive Navbar */}
        <ResponsiveNavbar
          isConnected={agentConnectionStatus?.isAgentConnected ?? false}
          connection={{
            status: agentConnectionStatus?.isAgentConnected
              ? "connected"
              : "disconnected",
            message: agentConnectionStatus?.isAgentConnected
              ? t("chatjess.status.online")
              : t("chatjess.status.offline"),
          }}
          isDarkTheme={isDarkTheme}
          onThemeToggle={toggleTheme}
          isSidebarCollapsed={isSidebarCollapsed}
          profileImage={
            agentConnectionStatus?.isAgentConnected
              ? jessProfileIcon
              : jessProfileIconOff
          }
          profileName={jessNameLogo}
          agentConnectionStatus={agentConnectionStatus}
          showBackButton={false}
        />

        {/* KPI Summary Chip */}
        <button
          className={`${styles.kpiToggleButton} ${kpiPanelOpen ? styles.active : ""} ${!isDarkTheme ? styles.lightTheme : ""} ${kpiWsData ? styles.hasData : ""}`}
          onClick={() => setKpiPanelOpen((prev) => !prev)}
          aria-label={t("kpiPanel.toggleButton")}
          aria-expanded={kpiPanelOpen}
          aria-hidden={kpiPanelOpen ? true : undefined}
          tabIndex={kpiPanelOpen ? -1 : 0}
          title={t("kpiPanel.toggleButton")}
        >
          <i className="fas fa-chart-bar" />
          {kpiWsData?.summary ? (
            <span className={styles.kpiChipLabel}>
              <span className={styles.kpiChipValue}>
                {String(kpiWsData.summary.value)}
              </span>
              <span className={styles.kpiChipText}>
                {resolveI18nLabel(kpiWsData.summary.label, kpiLang)}
              </span>
            </span>
          ) : (
            <span className={styles.kpiChipLabel}>
              {t("kpiPanel.toggleButton")}
            </span>
          )}
        </button>

        {/* Chat Content Container */}
        <div
          className={styles.chatContentContainer}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className={styles.chatContainer}>
            <div
              id="jess-messages-container"
              className={styles.messagesWrapper}
            >
              {isLoadingAgentStatus ? (
                // Loading skeleton while checking agent status
                <div className={styles.loadingContainer}>
                  <div className={styles.loadingSpinner}>
                    <i
                      className="fas fa-spinner fa-spin"
                      style={{ fontSize: "32px", color: "#8b5cf6" }}
                    />
                  </div>
                  <p className={styles.loadingText}>
                    {t("chatjess.loading.checkingJessAvailability")}
                  </p>
                </div>
              ) : isLoadingChatHistory && displayMessages.length === 0 ? (
                // Loading animation while loading chat history (only if no messages)
                <div className={styles.loadingContainer}>
                  <div className={styles.loadingAnimation}>
                    <div className={styles.jessAvatarPulse}>
                      <img src={jessImage} alt="Jess" />
                    </div>
                    <div className={styles.loadingDots}>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                  <p className={styles.loadingText}>
                    {t("chatjess.loading.connectingToJess")}
                  </p>
                  <p className={styles.loadingSubtext}>
                    {chatHistoryRetryCount > 0
                      ? t("chatjess.loading.retryingConnection", {
                          attempt: chatHistoryRetryCount,
                          max: maxRetryAttempts,
                        })
                      : t("chatjess.loading.loadingHistory")}
                  </p>
                </div>
              ) : chatHistoryTimeout && displayMessages.length === 0 ? (
                // Timeout message when chat history takes too long
                <div className={styles.timeoutContainer}>
                  <div className={styles.timeoutIcon}>
                    <i
                      className="fas fa-clock"
                      style={{ fontSize: "48px", color: "#f59e0b" }}
                    />
                  </div>
                  <h3 className={styles.timeoutTitle}>
                    {t("chatjess.timeout.title")}
                  </h3>
                  <p className={styles.timeoutMessage}>
                    {t("chatjess.timeout.message")}
                  </p>
                  <p className={styles.timeoutInstruction}>
                    {t("chatjess.timeout.instruction")}
                  </p>
                </div>
              ) : showWelcomeAnimation && displayMessages.length === 0 ? (
                // Show welcome message bubble for new users (only if no real messages)
                <div className={styles.messagesWrapper}>
                  {/* Balão de digitação inicial */}
                  {showTyping1 && (
                    <div className={styles.messageJess}>
                      <div className={styles.messageContent}>
                        <div className={styles.typingIndicator}>
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mensagem de boas-vindas */}
                  {showMessage1 && (
                    <div className={styles.messageJess}>
                      <div className={styles.messageContent}>
                        <p>{t("chatjess.ui.welcomeMessage")}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : shouldShowOfflineIndicator() &&
                displayMessages.length === 0 ? (
                // Show offline indicator if agent is offline and no messages
                <div className={styles.offlineIndicatorContainer}>
                  <div className={styles.offlineIndicator}>
                    <div className={styles.offlineIconWrapper}>
                      <i
                        className="fas fa-wifi"
                        style={{ fontSize: "48px", color: "#ff4757" }}
                      />
                    </div>
                    <h3 className={styles.offlineTitle}>
                      {t("chatjess.offline.modalTitle")}
                    </h3>
                    <p className={styles.offlineMessage}>
                      {t("chatjess.offline.modalMessage")}
                    </p>
                    <p className={styles.offlineInstruction}>
                      {t("chatjess.offline.modalInstruction")}
                    </p>
                    <p className={styles.offlineDownload}>
                      {t("chatjess.offline.modalDownloadText")}{" "}
                      <button
                        type="button"
                        className={styles.offlineLink}
                        onClick={() => {
                          // TODO: Implement download functionality
                        }}
                      >
                        {t("chatjess.offline.modalDownloadLink")}
                      </button>
                    </p>
                    <button
                      type="button"
                      className={styles.offlineCloseButton}
                      onClick={() => setHasClosedOfflineIndicator(true)}
                    >
                      {t("chatjess.offline.close")}
                    </button>
                  </div>
                </div>
              ) : (
                // Show normal chat content
                <>
                  <div id="jess-messages-container">
                    <ChatJessMessages
                      messages={displayMessages}
                      onFeedbackClick={handleFeedbackClick}
                      feedbackOpen={feedbackOpen}
                      openFeedbackMessageId={feedbackMsg?.id || null}
                      isDarkTheme={isDarkTheme}
                    />
                  </div>

                  {(wsIsTyping || isWaitingForResponse) && (
                    <div
                      className={styles.messageJess}
                      style={{
                        minWidth: "120px",
                        maxWidth: "120px",
                        marginBottom: "16px",
                        alignSelf: "flex-start",
                        marginRight: "auto",
                      }}
                    >
                      <div
                        className={styles.messageContent}
                        style={{ padding: "8px 16px" }}
                      >
                        <div className={styles.typingIndicator}>
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Message Input - also a drop zone so user can add more files by dropping when attachments exist */}
        <div
          className={inputClass}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Preview de Anexos Pendentes - Modal Externa */}
          {pendingAttachments.length > 0 && (
            <div
              className={`${styles.pendingAttachmentsModal} ${isSidebarCollapsed ? styles.sidebarCollapsed : ""} ${inputClass.includes("expanded") ? styles.expanded : ""}`}
            >
              <div className={styles.pendingAttachmentsHeader}>
                <span className={styles.pendingAttachmentsTitle}>
                  <i
                    className="fas fa-paperclip"
                    style={{ marginRight: "8px", fontSize: "12px" }}
                  />
                  {t(
                    `chatjess.attachments.selected${pendingAttachments.length > 1 ? "_plural" : ""}`,
                    { count: pendingAttachments.length },
                  )}
                </span>
                <button
                  className={styles.clearAllAttachmentsBtn}
                  onClick={() => {
                    pendingAttachments.forEach((attachment) =>
                      removeAttachment(attachment.id),
                    );
                  }}
                  title={t("chatjess.attachments.cancelAll")}
                >
                  <i className="fas fa-times" />
                  {t("chatjess.attachments.cancelAll")}
                </button>
              </div>

              <div className={styles.pendingAttachmentsContainer}>
                {pendingAttachments.map((attachment) => (
                  <div key={attachment.id} className={styles.attachmentPreview}>
                    <div className={styles.attachmentThumbnail}>
                      {attachment.isUploading ? (
                        <div
                          style={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <i
                            className={getFileIcon(
                              attachment.type,
                              attachment.name,
                            )}
                            style={{
                              color: getFileIconColor(
                                attachment.type,
                                attachment.name,
                              ),
                              opacity: 0.5,
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              fontSize: "12px",
                              color: "#666",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                            }}
                          >
                            d
                            <i className="fas fa-spinner fa-spin" />
                          </div>
                        </div>
                      ) : attachment.thumbnail ? (
                        <img src={attachment.thumbnail} alt={attachment.name} />
                      ) : (
                        <i
                          className={getFileIcon(
                            attachment.type,
                            attachment.name,
                          )}
                          style={{
                            color: getFileIconColor(
                              attachment.type,
                              attachment.name,
                            ),
                          }}
                        />
                      )}
                    </div>
                    <div className={styles.attachmentInfo}>
                      <div className={styles.attachmentName}>
                        {attachment.name}
                        {attachment.isUploading && (
                          <span
                            style={{
                              color: "#666",
                              fontSize: "12px",
                              marginLeft: "8px",
                            }}
                          >
                            Enviando...
                          </span>
                        )}
                      </div>
                      <div className={styles.attachmentSize}>
                        {formatFileSize(attachment.size)}
                      </div>
                    </div>
                    <button
                      className={styles.attachmentRemove}
                      onClick={() => removeAttachment(attachment.id)}
                      title={t("chatjess.attachments.removeAttachment")}
                      disabled={attachment.isUploading}
                      style={{ opacity: attachment.isUploading ? 0.5 : 1 }}
                    >
                      <i className="fas fa-times" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.inputForm}>
            {shouldShowAttachmentAndEmojiButtons() && (
              <button
                id="jess-attachment-button"
                type="button"
                className={`${styles.inputButton} ${styles.attachmentButton} attachment-button ${showAttachmentModal ? styles.rotated : ""}`}
                title={t("chatjess.ui.attachFiles")}
                onClick={handleAttachmentButtonClick}
              >
                <i className="fas fa-plus" />
              </button>
            )}

            {shouldShowAttachmentAndEmojiButtons() && (
              <button
                type="button"
                className={`${styles.inputButton} emoji-button`}
                onClick={handleEmojiButtonClick}
                title={t("chatjess.ui.addEmoji")}
                style={{ position: "relative" }}
              >
                <i className="fas fa-smile" />
              </button>
            )}

            {audioRecorderState === "idle" ? (
              <>
                <textarea
                  rows={1}
                  id="jess-message-input"
                  ref={inputRef}
                  value={currentMessage}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyPress}
                  onPaste={handlePaste}
                  onFocus={() => {
                    setIsInputFocused(true);
                  }}
                  onBlur={() => setIsInputFocused(false)}
                  placeholder={
                    pendingAttachments.length > 0
                      ? t("chatjess.ui.typeCaption")
                      : isInputFocused
                        ? t("chatjess.ui.typeMessageFocused")
                        : t("chatjess.ui.typeMessage")
                  }
                  className={`${styles.messageInput} ${!isDarkTheme ? styles.lightTheme : ""} ${pendingAttachments.length > 0 ? styles.withAttachments : ""}`}
                />

                {/* Botão DENTRO da barra com animação */}
                <div className={styles.sendButtonContainer}>
                  {renderSendButton()}
                </div>
              </>
            ) : (
              <div className={styles.audioRecorderContainer}>
                <AudioRecorder
                  onAudioRecorded={handleAudioRecorded}
                  onCancel={handleCancelRecording}
                  isDarkTheme={isDarkTheme}
                />
              </div>
            )}
          </div>

          {/* Modal de Anexos */}
          {showAttachmentModal && (
            <div className={`${styles.attachmentModal} attachment-modal`}>
              <div
                className={styles.attachmentOption}
                onClick={() => handleAttachmentSelect("document")}
              >
                <div className={styles.attachmentIcon}>
                  <i className="fas fa-file-alt" />
                </div>
                <span>{t("chatjess.attachments.document")}</span>
              </div>

              <div
                className={styles.attachmentOption}
                onClick={() => handleAttachmentSelect("image")}
              >
                <div className={styles.attachmentIcon}>
                  <i className="fas fa-image" />
                </div>
                <span>{t("chatjess.attachments.image")}</span>
              </div>

              <div
                className={styles.attachmentOption}
                onClick={() => handleAttachmentSelect("audio")}
              >
                <div className={styles.attachmentIcon}>
                  <i className="fas fa-microphone" />
                </div>
                <span>{t("chatjess.attachments.audio")}</span>
              </div>
            </div>
          )}

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div
              className={`emoji-picker-container${isDarkTheme ? " emoji-picker-dark" : ""}`}
              style={{
                position: "absolute",
                bottom: "60px",
                left: "60px",
                zIndex: 20,
                borderRadius: "8px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              }}
            >
              <Picker
                onEmojiClick={handleEmojiSelect}
                theme={isDarkTheme ? Theme.DARK : Theme.LIGHT}
              />
            </div>
          )}
        </div>
      </div>

      <FeedbackPanel
        activeTab={activeTab}
        closeFeedbackPanel={closeFeedbackPanel}
        feedbackMsg={feedbackMsg}
        feedbackPanelClass={feedbackPanelClass}
        handleSubmitAnswer={handleSubmitAnswer}
        handleSuggestionTextChange={handleSuggestionTextChange}
        setActiveTab={setActiveTab}
        suggestionText={suggestionText}
      />

      <KpiPanel
        isOpen={kpiPanelOpen}
        onClose={() => setKpiPanelOpen(false)}
        isDarkTheme={isDarkTheme}
        isSidebarCollapsed={isSidebarCollapsed}
        useMock={useMock}
        wsData={kpiWsData ?? undefined}
      />

      {/* Drag & Drop Overlay - click outside the card to dismiss */}
      {isDragOver && (
        <div
          className={styles.dragDropOverlay}
          onClick={() => setIsDragOver(false)}
          role="presentation"
        >
          <div
            className={styles.dragDropContent}
            onClick={(e) => e.stopPropagation()}
          >
            <i
              className="fas fa-cloud-upload-alt"
              style={{ fontSize: "48px" }}
            />
            <div>{t("chatjess.attachments.dropFilesHere")}</div>
            <div>{t("chatjess.attachments.supportedFileTypes")}</div>
          </div>
        </div>
      )}

      {/* Modal de Contexto da Imagem */}
      {imageContextModal.show && (
        <div
          className={`${styles.imageContextModal} image-context-modal`}
          style={{
            position: "fixed",
            left: `${imageContextModal.position.x}px`,
            top: `${imageContextModal.position.y}px`,
            zIndex: 1000,
          }}
        >
          <div
            className={styles.contextOption}
            onClick={() => {
              const attachment = displayMessages
                .flatMap((msg) => msg.attachments || [])
                .find((att) => att.id === imageContextModal.attachmentId);
              if (attachment) handleDownloadImage(attachment);
            }}
          >
            <i className="fas fa-download" />
            <span>Baixar</span>
          </div>
          <div
            className={styles.contextOption}
            onClick={() => {
              const attachment = displayMessages
                .flatMap((msg) => msg.attachments || [])
                .find((att) => att.id === imageContextModal.attachmentId);
              if (attachment && attachment.url) {
                navigator.clipboard.writeText(attachment.url);
                closeImageContextModal();
              }
            }}
          >
            <i className="fas fa-copy" />
            <span>Copiar link</span>
          </div>
          <div
            className={styles.contextOption}
            onClick={() => {
              const attachment = displayMessages
                .flatMap((msg) => msg.attachments || [])
                .find((att) => att.id === imageContextModal.attachmentId);
              if (attachment && attachment.url) {
                window.open(attachment.url, "_blank");
                closeImageContextModal();
              }
            }}
          >
            <i className="fas fa-external-link-alt" />
            <span>Abrir em nova aba</span>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      <DetailModal
        isOpen={showDetailModal}
        onClose={closeDetailModal}
        title={detailModalTitle}
        content={detailModalContent}
      />

      {/* Painel "Passo a Passo" - Detalhes da Pergunta */}
      <aside
        id="jess-step-by-step-panel"
        className={`${styles.feedbackPanel} ${stepByStepPanelOpen ? styles.open : ""}`}
      >
        <div className={styles.feedbackPanelHeader}>
          <h2 className={styles.feedbackPanelTitle}>
            {t("chatjess.stepByStepPanel.title")}
          </h2>
          <button
            className={styles.feedbackPanelClose}
            onClick={() => setStepByStepPanelOpen(false)}
          >
            ×
          </button>
        </div>

        <div className={styles.feedbackPanelTabs}>
          <button
            className={`${styles.feedbackPanelTab} ${activeStepByStepTab === "report" ? styles.active : ""}`}
            onClick={() => setActiveStepByStepTab("report")}
          >
            {t("chatjess.stepByStepPanel.reportTab")}
          </button>
          <button
            className={`${styles.feedbackPanelTab} ${activeStepByStepTab === "attachments" ? styles.active : ""}`}
            onClick={() => setActiveStepByStepTab("attachments")}
          >
            {t("chatjess.stepByStepPanel.attachmentsTab")}
          </button>
        </div>

        {/* Aba Relatório */}
        {activeStepByStepTab === "report" && (
          <div
            className={`${styles.feedbackPanelTabContent} ${styles.reportTab} ${styles.active}`}
          >
            <div className={styles.feedbackSection}>
              <h2 className={styles.feedbackSectionTitle}>
                {t("chatjess.stepByStepPanel.installationTitle")}
              </h2>
              <div className={styles.feedbackContext}>
                <strong>Passo a Passo:</strong>{" "}
                {t("chatjess.stepByStepPanel.installationDescription")}
              </div>
            </div>

            <div className={styles.feedbackSection}>
              <h3 className={styles.feedbackSectionSubtitle}>
                {t("chatjess.stepByStepPanel.step1Title")}
              </h3>
              <div className={styles.feedbackContext}>
                <strong>1.</strong> {t("chatjess.stepByStepPanel.step1Item1")}
                <br />
                <strong>2.</strong> {t("chatjess.stepByStepPanel.step1Item2")}
                <br />
                <strong>3.</strong> {t("chatjess.stepByStepPanel.step1Item3")}
              </div>
            </div>

            <div className={styles.feedbackSection}>
              <h3 className={styles.feedbackSectionSubtitle}>
                {t("chatjess.stepByStepPanel.step2Title")}
              </h3>
              <div className={styles.feedbackContext}>
                <strong>1.</strong> {t("chatjess.stepByStepPanel.step2Item1")}
                <br />
                <strong>2.</strong> {t("chatjess.stepByStepPanel.step2Item2")}
                <br />
                <strong>3.</strong> {t("chatjess.stepByStepPanel.step2Item3")}
                <br />
                <strong>4.</strong> {t("chatjess.stepByStepPanel.step2Item4")}
              </div>
            </div>

            <div className={styles.feedbackSection}>
              <h3 className={styles.feedbackSectionSubtitle}>
                {t("chatjess.stepByStepPanel.step3Title")}
              </h3>
              <div className={styles.feedbackContext}>
                <strong>1.</strong> {t("chatjess.stepByStepPanel.step3Item1")}
                <br />
                <strong>2.</strong> {t("chatjess.stepByStepPanel.step3Item2")}
                <br />
                <strong>3.</strong> {t("chatjess.stepByStepPanel.step3Item3")}
                <br />
                <strong>4.</strong> {t("chatjess.stepByStepPanel.step3Item4")}
              </div>
            </div>

            <div className={styles.feedbackSection}>
              <h3 className={styles.feedbackSectionSubtitle}>
                {t("chatjess.stepByStepPanel.step4Title")}
              </h3>
              <div className={styles.feedbackContext}>
                <strong>1.</strong> {t("chatjess.stepByStepPanel.step4Item1")}
                <br />
                <strong>2.</strong> {t("chatjess.stepByStepPanel.step4Item2")}
                <br />
                <strong>3.</strong> {t("chatjess.stepByStepPanel.step4Item3")}
                <br />
                <strong>4.</strong> {t("chatjess.stepByStepPanel.step4Item4")}
                {/* Botão Avançar dentro do Passo 4 */}
                {isTourRunning && currentStep === 2 && (
                  <button
                    id="jess-step-by-step-next-button"
                    className={`${styles.tourNextButton} ${styles.pulseAnimation}`}
                    onClick={() => {
                      // Avançar o tour primeiro
                      tourNextStep();

                      // Redirecionar para channel-session após delay maior para garantir que o estado foi salvo
                      setTimeout(() => {
                        navigate("/channel-session");
                      }, 1000); // Aumentado de 500ms para 1000ms
                    }}
                    style={{ marginTop: "16px" }}
                  >
                    ⚡ Avançar ⚡
                  </button>
                )}
              </div>
            </div>

            <div className={styles.feedbackSection}>
              <h3 className={styles.feedbackSectionSubtitle}>
                {t("chatjess.stepByStepPanel.infoTitle")}
              </h3>
              <div className={styles.feedbackContext}>
                <strong>• Compatibilidade:</strong>{" "}
                {t("chatjess.stepByStepPanel.infoCompatibility")}
                <br />
                <strong>• Requisitos:</strong>{" "}
                {t("chatjess.stepByStepPanel.infoRequirements")}
                <br />
                <strong>• Suporte:</strong>{" "}
                {t("chatjess.stepByStepPanel.infoSupport")}
                <br />
                <strong>• Atualizações:</strong>{" "}
                {t("chatjess.stepByStepPanel.infoUpdates")}
              </div>
            </div>
          </div>
        )}

        {/* Aba Anexos */}
        {activeStepByStepTab === "attachments" && (
          <div
            className={`${styles.feedbackPanelTabContent} ${styles.attachmentsTab} ${styles.active}`}
          >
            <div className={styles.feedbackSection}>
              <h2 className={styles.feedbackSectionTitle}>
                📎 {t("chatjess.stepByStepPanel.attachmentsTitle")}
              </h2>
              <div className={styles.feedbackContext}>
                {t("chatjess.stepByStepPanel.attachmentsDescription")}
              </div>
            </div>

            <div className={styles.feedbackSection}>
              <div className={styles.attachmentItem}>
                <div className={styles.attachmentHeader}>
                  <div className={styles.attachmentIcon}>
                    <img
                      src={jessDesktopAppIcon}
                      alt="Jesss App"
                      style={{
                        width: "24px",
                        height: "24px",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                  <div className={styles.attachmentInfo}>
                    <div className={styles.attachmentName}>
                      {t("chatjess.welcome.appName")}
                    </div>
                    <div className={styles.attachmentSize}>
                      {t("chatjess.welcome.appSize")}
                    </div>
                  </div>
                </div>
                <div className={styles.attachmentDescription}>
                  {t("chatjess.stepByStepPanel.appDescription")}
                </div>
                <div className={styles.attachmentActions}>
                  <button className={styles.attachmentDownloadBtn}>
                    <i className="fas fa-download"></i>
                    {t("chatjess.stepByStepPanel.downloadButton")}
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.feedbackSection}>
              <h3 className={styles.feedbackSectionSubtitle}>
                ℹ️ {t("chatjess.stepByStepPanel.downloadInfoTitle")}
              </h3>
              <div className={styles.feedbackContext}>
                <strong>
                  • {t("chatjess.stepByStepPanel.downloadInfoVersion")}:
                </strong>{" "}
                v2.1.0
                <br />
                <strong>
                  • {t("chatjess.stepByStepPanel.downloadInfoDate")}:
                </strong>{" "}
                {new Date().toLocaleDateString()}
                <br />
                <strong>
                  • {t("chatjess.stepByStepPanel.downloadInfoChecksum")}:
                </strong>{" "}
                SHA256: a1b2c3d4e5f6...
                <br />
                <strong>
                  • {t("chatjess.stepByStepPanel.downloadInfoSecurity")}:
                </strong>{" "}
                {t("chatjess.stepByStepPanel.downloadInfoSecurityText")}
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Offline Notification */}
      {showOfflineNotification && (
        <div className={`${styles.offlineNotification} ${styles.show}`}>
          <i className="fas fa-info-circle" style={{ marginRight: "8px" }} />
          {t("chatjess.offline.notificationMessage")}
        </div>
      )}

      {/* Tour Not Available Notification */}
      {/* Elementos especiais para o tour */}
      <div
        id="jess-tour-welcome"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "1px",
          height: "1px",
          visibility: "hidden",
        }}
      />
      <div
        id="jess-tour-completion"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "1px",
          height: "1px",
          visibility: "hidden",
        }}
      />

      {/* Notificação de tour não disponível (para manter compatibilidade) */}
      <div
        className={`${styles.tourNotification} ${showTourNotification ? styles.show : ""}`}
      >
        <div className={styles.tourNotificationContent}>
          <div className={styles.tourNotificationHeader}>
            <i className="fas fa-construction" style={{ marginRight: "8px" }} />
            <span className={styles.tourNotificationTitle}>
              {t("chatjess.welcome.tourNotAvailableTitle")}
            </span>
            <button
              className={styles.tourNotificationClose}
              onClick={() => setShowTourNotification(false)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className={styles.tourNotificationBody}>
            <p>{t("chatjess.welcome.tourNotAvailableMessage")}</p>
            <div className={styles.tourNotificationSuggestions}>
              <p>
                <strong>
                  {t("chatjess.welcome.tourNotAvailableAlternatives")}:
                </strong>
              </p>
              <ul>
                <li>{t("chatjess.welcome.tourNotAvailableOption1")}</li>
                <li>{t("chatjess.welcome.tourNotAvailableOption2")}</li>
                <li>{t("chatjess.welcome.tourNotAvailableOption3")}</li>
              </ul>
            </div>
            <p className={styles.tourNotificationThanks}>
              {t("chatjess.welcome.tourNotAvailableThanks")}
            </p>
          </div>
        </div>
      </div>

      {/* Tour Interativo */}
      <Joyride
        steps={tourSteps}
        run={isTourRunning}
        stepIndex={currentStep}
        callback={handleJoyrideCallback}
        continuous={true}
        showSkipButton={true}
        showProgress={true}
        disableOverlayClose={true}
        spotlightClicks={true}
        hideCloseButton={true}
        hideBackButton={true}
        debug={true}
        key={`tour-${currentStep}`}
        styles={{
          options: {
            primaryColor: "#83de80",
            zIndex: 10000,
            arrowColor: "transparent", // Remover seta padrão
          },
          tooltip: {
            display: "none", // Esconder tooltip padrão, usar JessModal
          },
          tooltipContent: {
            display: "none",
          },
          tooltipContainer: {
            display: "none",
          },
          overlay: {
            backgroundColor: "transparent", // Remover overlay para permitir interação
            backdropFilter: "none",
          },
          spotlight: {
            borderRadius: "16px",
            boxShadow: `
               0 0 40px rgba(131, 222, 128, 0.8),
               0 0 80px rgba(131, 222, 128, 0.4),
               inset 0 0 20px rgba(131, 222, 128, 0.2)
             `,
            border: "3px solid rgba(131, 222, 128, 0.6)",
            animation: "spotlightPulse 3s ease-in-out infinite",
            // Permitir scroll no elemento destacado
            overflow: "auto",
            pointerEvents: "auto",
          },
        }}
      />

      {/* Modal da Jess para o Tour */}
      <JessModal
        isOpen={isTourRunning}
        currentStep={currentStep}
        stepIndex={currentStep}
        steps={tourSteps}
        totalSteps={totalSteps}
        isWaitingForInteraction={isWaitingForInteraction}
        canGoNext={canGoNext}
        canGoBack={canGoBack}
        onNext={tourNextStep}
        onBack={tourPreviousStep}
        onSkip={skipTour}
        onClose={stopTour}
        targetSelector={getCurrentTarget()}
      />

      {/* Cursor Animado do Tour - Não mostrar no Step 3 */}
      <TourAnimatedCursor
        targetSelector={getCurrentTarget()}
        isVisible={(() => {
          return isTourRunning && currentStep !== 2;
        })()}
        isWaitingForInteraction={isWaitingForInteraction}
        onActionCompleted={() => {}}
      />
    </div>
  );
};

export default ChatJessPage;
